import React from "react";
import { Star, FileText, Clock, Download, ExternalLink, Building2, CircleCheck } from "lucide-react";
import { Divider } from "antd";
import HeaderComponent from "components/card/HeaderComponent";

const OpenMessageCard = ({ message, isStarred, onToggleStar }) => {
  if (!message) return null;

  const getInitials = (name) =>
    name?.split(" ").slice(0, 2).map((n) => n[0]).join("") || "";

  const priorityClass = message.priority?.toLowerCase().replace(/\s+/g, "-");

  return (
    <HeaderComponent
      title={message.title}
      bgcolor="gradient light-indigo"
      cardBorder="ring-2 ring-blue-500"
      description={
        <div className="flex flex-col gap-1">
          <div className="inline-flex gap-1 items-center">
            <p>From:</p>
            <p className="text-sm font-medium">{message.sender}</p>
          </div>
          <div className="inline-flex gap-1 items-center">
            <p className={`text-[10px] status-indicator ${priorityClass}`}>
              {message.priority}
            </p>
            <p className="text-xs inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {message.date} at {message.time}
            </p>
          </div>
        </div>
      }
      icon={
        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-500 text-white font-semibold">
          {getInitials(message.sender)}
        </span>
      }
      rightNav={
        <div className="flex gap-3">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar();
            }}
          >
            <Star className={`w-4 h-4 ${isStarred ? "fill-yellow-500 text-yellow-500" : "text-slate-700"}`} />
          </button>
          <div className="inline-flex items-center justify-center rounded-md h-8 w-8">
            <CircleCheck className="w-4 h-4 text-slate-700" />
          </div>
        </div>
      }
    >
      <div className="max-h-[60vh] overflow-y-auto">
        <div className="text-sm whitespace-pre-line my-8">{message.content}</div>
      </div>

      <Divider style={{ margin: "0.5rem 0" }} />

      {message.attachments?.length > 0 && (
        <div className="mt-3">
          <h4 className="font-semibold text-sm mb-1 inline-flex items-center gap-1">
            <FileText className="w-3.5 h-3.5 text-blue-600" /> Attachments ({message.attachments.length})
          </h4>
          <ul className="space-y-1">
            {message.attachments.map((att, index) => (
              <li
                key={index}
                className="flex items-center justify-between p-2 border rounded-xl hover:bg-blue-50 gap-2"
              >
                <span className="inline-flex items-center gap-2">
                  <div className="p-1.5 rounded-xl bg-blue-100 text-blue-600 flex-shrink-0">
                    <FileText className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm">{att.name}</span>
                    <p className="text-[10px]">PDF Document</p>
                  </div>
                </span>
                <div className="flex gap-1 flex-shrink-0">
                  <button className="inline-flex items-center justify-center whitespace-nowrap font-medium hover:bg-gray-200 rounded-xl gap-1.5 h-7 sm:h-7 text-[10px] sm:text-[11px] px-2">
                    <Download className="w-3 h-3" />
                    Download
                  </button>
                  <button className="inline-flex items-center justify-center whitespace-nowrap font-medium hover:bg-gray-200 rounded-xl gap-1.5 h-7 sm:h-7 text-[10px] sm:text-[11px] px-2">
                    <ExternalLink className="w-3 h-3" />
                    View
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
        <div className="flex items-start gap-2">
          <Building2 className="w-3.5 h-3.5 text-blue-600" />
          <div>
            <p className="text-[11px] sm:text-[12px] text-blue-900 font-medium">
              Official Communication from Hyvid
            </p>
            <p className="text-[10px] sm:text-[11px] text-blue-700 mt-0.5">
              This is a one-way communication channel. For inquiries, please contact your account manager or support team directly.
            </p>
          </div>
        </div>
      </div>
    </HeaderComponent>
  );
};

export default OpenMessageCard;
